import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { registerSchema, type RegisterFormData } from '../../lib/validationSchemas';
import { generateFingerprint } from '../../lib/fingerprintUtils';
import SEO from '../../components/seo/SEO';
import { trackRegistration } from '../../lib/gtag';
import { trackConversion } from '../../lib/analytics';
import { getStoredAttribution } from '../../lib/attribution';

export default function RegisterPage() {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register: registerUser, error, clearError, isLoading } = useAuthStore();
  const referralCode = searchParams.get('ref') ?? getStoredAttribution()?.ref ?? '';
  const [deviceFingerprint, setDeviceFingerprint] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      accept_terms: false,
      referral_code: referralCode || undefined,
      telegram: '',
    },
  });

  // Generate browser fingerprint on component mount
  useEffect(() => {
    generateFingerprint().then(setDeviceFingerprint);
  }, []);

  const onSubmit = async (data: RegisterFormData) => {
    clearError();

    try {
      const attribution = getStoredAttribution();
      await registerUser({
        ...data,
        device_fingerprint: deviceFingerprint,
        ...attribution,
      });
      trackRegistration();
      trackConversion('trial_signup', attribution ?? undefined);
      navigate('/verify-email');
    } catch {
      // Error is handled by the store
    }
  };

  return (
    <>
      <SEO
        title="Register | Axetra"
        description="Create your Axetra account."
        noindex={true}
      />
      <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
        {t('register.title')}
      </h2>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <input type="hidden" {...register('referral_code')} />
        <div>
          <label htmlFor="name" className="label">
            {t('register.name_label')}
          </label>
          <div className="mt-1">
            <input
              id="name"
              type="text"
              autoComplete="name"
              {...register('name')}
              className={`input ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="label">
            {t('register.email_label')}
          </label>
          <div className="mt-1">
            <input
              id="email"
              type="email"
              autoComplete="email"
              {...register('email')}
              className={`input ${errors.email ? 'border-red-500' : ''}`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="telegram" className="label">
            {t('register.telegram_label')}
          </label>
          <div className="mt-1 relative">
            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 text-sm pointer-events-none">@</span>
            <input
              id="telegram"
              type="text"
              autoComplete="off"
              placeholder={t('register.telegram_placeholder')}
              {...register('telegram')}
              className={`input pl-7 ${errors.telegram ? 'border-red-500' : ''}`}
            />
          </div>
          {errors.telegram && (
            <p className="mt-1 text-sm text-red-600">{errors.telegram.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="label">
            {t('register.password_label')}
          </label>
          <div className="mt-1">
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              {...register('password')}
              className={`input ${errors.password ? 'border-red-500' : ''}`}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="password_confirmation" className="label">
            {t('register.confirm_password_label')}
          </label>
          <div className="mt-1">
            <input
              id="password_confirmation"
              type="password"
              autoComplete="new-password"
              {...register('password_confirmation')}
              className={`input ${errors.password_confirmation ? 'border-red-500' : ''}`}
            />
            {errors.password_confirmation && (
              <p className="mt-1 text-sm text-red-600">{errors.password_confirmation.message}</p>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="accept_terms"
                type="checkbox"
                {...register('accept_terms')}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="accept_terms" className="text-sm text-gray-700">
                {t('register.accept_terms_label')}{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  {t('register.terms_link')}
                </Link>
              </label>
              {errors.accept_terms && (
                <p className="mt-1 text-sm text-red-600">{errors.accept_terms.message}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? t('register.submitting') : t('register.submit_button')}
          </button>
        </div>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600">
        {t('register.have_account')}{' '}
        <Link
          to={`/${i18n.language}/login`}
          className="font-medium text-indigo-600 hover:text-indigo-500"
        >
          {t('register.login_link')}
        </Link>
      </p>
    </>
  );
}
